import { useEffect, useState } from "react";
import BigNumber from "bignumber.js";
import { v4 as uuid } from "uuid";
import { api } from "../../../../../../../api";
import {
  actions,
  useAppDispatch,
  useAppSelector,
} from "../../../../../../../redux";
import {
  poolsContractService,
  stakingService,
} from "../../../../../../../services";
import { toast } from "../../../../../../../toasts";
import { useEthersSigner } from "../../../../../../../utils";
import { setCurrentTfaAction } from "../../../../../../../redux/features/userSlice";
import { openTfaModal } from "../../../../../../../redux/features/uiSlice";

export type RewardToClaim = {
  id: string;
  type: "POOL" | "STAKING";
  usdcRewards: string;
  soilRewards: string;
  poolId: number;
  level: number;
  name: string;
  isClaimPending: boolean;
  isClaimCompleted: boolean;
  isAvailableToClaim: boolean;
};

export const useClaimAllRewardsHandler = (onClaimSuccess: () => void) => {
  const [rewardsToClaim, setRewardsToClaim] = useState<RewardToClaim[]>();
  const [isRewardsLoading, setIsRewardsLoading] = useState(true);
  const [isClaimInProgress, setIsClaimInProgress] = useState(false);

  const isStakingDataLoading = useAppSelector(
    (store) => store.staking.isLoading
  );
  const stakingRewards = useAppSelector((store) => store.staking.rewardsEarned);

  const dispatch = useAppDispatch();

  const { tfaEnabled, tfaAuthenticatedForAction } = useAppSelector(
    (state) => state.user
  );
  const { isClaimAllRewardsAuthenticated } = tfaAuthenticatedForAction;

  const signer = useEthersSigner();

  const fetchRewards = async () => {
    try {
      setIsRewardsLoading(true);
      const fetchedRewardsToClaim: RewardToClaim[] = [];
      let page = 0;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const {
          data: { data, total },
        } = await api.pools.getLevelsWithRewards(page, 20);
        data.forEach((rewardData) => {
          fetchedRewardsToClaim.push({
            id: uuid(),
            type: "POOL",
            name: rewardData.name,
            poolId: rewardData.pool.poolId,
            level: rewardData.level,
            soilRewards: rewardData.rewards
              .reduce(
                (acc, rewardItem) =>
                  acc.plus(rewardItem.soilAmount).plus(rewardItem.boostAmount),
                new BigNumber(0)
              )
              .toString(),
            usdcRewards: rewardData.rewards
              .reduce(
                (acc, rewardItem) => acc.plus(rewardItem.amount),
                new BigNumber(0)
              )
              .toString(),
            isClaimCompleted: false,
            isClaimPending: false,
            isAvailableToClaim: false,
          });
        });

        if (fetchedRewardsToClaim.length < total) {
          page += 1;
        } else {
          break;
        }
      }

      setRewardsToClaim((rewards) => {
        const isPoolsAdded = rewards?.some((reward) => reward.type === "POOL");

        if (isPoolsAdded) {
          return rewards;
        }

        return [...(rewards || []), ...fetchedRewardsToClaim];
      });
    } catch (e: unknown) {
      toast.errorHandler(e, "Failed to fetch the information about rewards");
    } finally {
      setIsRewardsLoading(false);
    }
  };

  useEffect(() => {
    fetchRewards();
  }, []);

  useEffect(() => {
    if (!isStakingDataLoading && Number(stakingRewards) > 0) {
      const stakingRewardObject = {
        id: uuid(),
        type: "STAKING" as const,
        name: "Staking",
        soilRewards: stakingRewards,
        usdcRewards: "0",
        poolId: 0,
        level: 0,
        isClaimCompleted: false,
        isClaimPending: false,
        isAvailableToClaim: false,
      };
      setRewardsToClaim((rewards) => {
        const isStakingAdded = rewards?.some(
          (reward) => reward.type === "STAKING"
        );

        if (isStakingAdded) {
          return rewards;
        }

        return [...(rewards || []), stakingRewardObject];
      });
    }
  }, [isStakingDataLoading]);

  const stakingFee = useAppSelector((store) => store.staking.feeSOIL);
  const poolsFee = useAppSelector(
    (store) => store.pool.poolsContractFee || "0"
  );

  useEffect(() => {
    setRewardsToClaim((rewards) =>
      rewards?.map((reward) => {
        if (reward.type === "POOL") {
          return {
            ...reward,
            isAvailableToClaim: new BigNumber(reward.usdcRewards).isGreaterThan(
              poolsFee
            ),
          };
        }
        return {
          ...reward,
          isAvailableToClaim: new BigNumber(reward.soilRewards).isGreaterThan(
            stakingFee
          ),
        };
      })
    );
  }, [stakingFee, rewardsToClaim?.length, poolsFee]);

  useEffect(() => {
    dispatch(actions.pool.getPoolsContractFee());
    dispatch(actions.staking.getStakingData());
  }, []);

  const handleClaim = async () => {
    if (!rewardsToClaim || !signer) {
      return;
    }

    setIsClaimInProgress(true);

    try {
      for (const rewardToClaim of rewardsToClaim) {
        if (!rewardToClaim.isAvailableToClaim) {
          continue;
        }

        // Mark reward object as pending
        setRewardsToClaim((rewards) =>
          rewards?.map((reward) => ({
            ...reward,
            isClaimPending: rewardToClaim.id === reward.id,
          }))
        );

        if (rewardToClaim.type === "STAKING") {
          const {
            data: { signature, deadline },
          } = await api.staking.getSignatureClaim();

          await stakingService.claimRewards(
            rewardToClaim.soilRewards,
            deadline,
            signature,
            signer
          );
        } else {
          const {
            data: { signature, deadline },
          } = await api.pools.getClaimRewardsSignature({
            poolId: rewardToClaim.poolId,
            level: rewardToClaim.level,
            rewards: rewardToClaim.usdcRewards,
            soilRewards: rewardToClaim.soilRewards,
            withWithdraw: false,
          });

          await poolsContractService.claimRewards(
            rewardToClaim.poolId,
            rewardToClaim.level,
            rewardToClaim.usdcRewards,
            rewardToClaim.soilRewards,
            deadline,
            signature,
            signer
          );
        }

        // Mark reward object as claimed
        setRewardsToClaim((rewards) =>
          rewards?.map((reward) => ({
            ...reward,
            isClaimPending: false,
            isClaimCompleted:
              reward.id === rewardToClaim.id ? true : reward.isClaimCompleted,
          }))
        );
      }

      toast.success("Claiming all rewards", "Rewards successfully claimed");
    } catch (e: unknown) {
      toast.errorHandler(e, "Failed to claim all rewards");
    }

    setIsClaimInProgress(false);
    onClaimSuccess();
  };

  const submitClaim = async () => {
    if (tfaEnabled) {
      dispatch(setCurrentTfaAction("claimAll"));
      dispatch(openTfaModal());
    } else {
      await handleClaim();
    }
  };

  useEffect(() => {
    if (isClaimAllRewardsAuthenticated) {
      handleClaim();
    }
  }, [isClaimAllRewardsAuthenticated]);

  return { rewardsToClaim, submitClaim, isClaimInProgress, isRewardsLoading };
};
